import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BotMessageSquare } from "lucide-react";

import ChatMessage from "./ChatMessage";
import type { ChatMessage as ChatMessageType, ChatThreadProps } from "../types/chat.types";

const ChatThread: React.FC<ChatThreadProps> = ({
  messages,
  isLoading = false,
  isStreaming = false,
  streamingMessageId,
  onRegenerate,
  onCopy,
  emptyState,
  record,
  showToolTrace = true,
  promptCatalog,
  onSelectSuggestedPrompt,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastSignature = useRef<string>("");

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const signature = `${messages.length}:${messages[messages.length - 1]?.content?.length ?? 0}`;
    if (signature === lastSignature.current) return;
    lastSignature.current = signature;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!isLoading) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-6"
        role="log"
        aria-label={t("Chat messages")}
      >
        {emptyState}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-4"
      role="log"
      aria-label={t("Chat messages")}
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        {messages.map((msg: ChatMessageType) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            isStreaming={isStreaming && msg.id === streamingMessageId}
            onRegenerate={
              onRegenerate && msg.role === "assistant" ? () => onRegenerate(msg.id) : undefined
            }
            onCopy={onCopy ? () => onCopy(msg.content) : undefined}
            record={record}
            showToolTrace={showToolTrace}
            promptCatalog={promptCatalog}
            onSelectSuggestedPrompt={onSelectSuggestedPrompt}
          />
        ))}
        {isLoading ? <AssistantSkeleton /> : null}
      </div>
    </div>
  );
};

const AssistantSkeleton: React.FC = () => (
  <div className="flex w-full gap-3" aria-hidden="true">
    <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
      <BotMessageSquare className="h-4 w-4" />
    </div>
    <div className="flex-1 space-y-2">
      <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200" />
      <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
    </div>
  </div>
);

export default ChatThread;
